import React, { useState, useEffect, useContext, useRef } from 'react';
import Swal from '../../utils/swal';
import ReactPaginate from 'react-paginate';
import { AppBar, Badge, Button, Checkbox, Collapse, Fab, FormControlLabel, Grow, IconButton, Toolbar } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import MidiaLabsCardSingle from "../../components/cards/midia-labs-card-single";
import MidiaLabsForm from "../../components/forms/midia-labs-form";
import AppContext from "../../../appContext";
import { useApi } from "../../../hooks/useApi";
import { Card } from "react-bootstrap";
import { Plus, Collection, NodeMinus } from "react-bootstrap-icons";
import EmptyState from "../../../shared/components/generic/empty-state/empty-state";
import { getNewIds } from "../../utils/diff-items";
import MESSAGES from "../../../shared/constants/messages";

const MidiaOticas = ({ selectedLab, hasUploadPermission, isLabUser, isActive }) => {
	const context = useContext(AppContext);
	const [midias, setMidias] = useState([]);
	const [totalMidias, setTotalMidias] = useState(0);
	const [showForm, setShowForm] = useState(false);
	const [editingMidia, setEditingMidia] = useState(null);
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(0);
	const [perPage] = useState(15);
	const [pageCount, setPageCount] = useState(0);
	const [selectedIds, setSelectedIds] = useState([]);
	const [selectAllMode, setSelectAllMode] = useState(false);
	const lastEffectiveCount = useRef(0);
	const [bannerContent, setBannerContent] = useState(null);
	const [newIds, setNewIds] = useState([]);
	const isFirstLoad = useRef(true);
	const { get, del, post } = useApi();
	const hasLoadedOnce = useRef(false);

	useEffect(() => {
		fetchMidias();
		hasLoadedOnce.current = true;
	}, [selectedLab, currentPage]);

	useEffect(() => {
		if (isActive && hasLoadedOnce.current) {
			fetchMidias();
		}
		if (!isActive) {
			setSelectedIds([]);
			setSelectAllMode(false);
		}
	}, [isActive]);

	useEffect(() => {
		setSelectedIds([]);
		setSelectAllMode(false);
	}, [selectedLab]);

	useEffect(() => {
		if (!selectAllMode) {
			setSelectedIds([]);
		}
	}, [currentPage]);

	const toggleSelect = (id, checked) => {
		if (selectAllMode) {
			setSelectAllMode(false);
			setSelectedIds(checked ? [id] : []);
			return;
		}
		setSelectedIds((prev) => {
			if (checked) {
				return prev.includes(id) ? prev : [...prev, id];
			}
			return prev.filter((item) => item !== id);
		});
	};

	const toggleSelectAll = (checked) => {
		if (!checked) {
			setSelectAllMode(false);
			setSelectedIds([]);
			return;
		}
		setSelectedIds(midias.map((m) => m.id));
	};

	const activateSelectAllMode = () => {
		setSelectAllMode(true);
	};

	const clearSelectAllMode = () => {
		setSelectAllMode(false);
		setSelectedIds([]);
	};

	const allSelected = midias.length > 0 && selectedIds.length === midias.length;
	const someSelected = selectedIds.length > 0 && selectedIds.length < midias.length;
	const hasMorePages = pageCount > 1;
	const showExpandBanner = !selectAllMode && allSelected && hasMorePages && totalMidias > midias.length;
	const showActiveBanner = selectAllMode;
	const effectiveCount = selectAllMode ? totalMidias : selectedIds.length;
	const showActionsRow = selectAllMode || selectedIds.length > 0;

	if (effectiveCount > 0) {
		lastEffectiveCount.current = effectiveCount;
	}

	useEffect(() => {
		if (showExpandBanner) {
			setBannerContent('expand');
		} else if (showActiveBanner) {
			setBannerContent('active');
		}
	}, [showExpandBanner, showActiveBanner]);

	const fetchMidias = async (showLoadingMessage = true) => {
		setLoading(true);

		if (showLoadingMessage) {
			context.showLoading('Carregando mídias...');
		}

		const labCnpj = isLabUser ? context.userSession.conta : selectedLab?.cnpj;

		const params = new URLSearchParams({
			lab_cnpj: labCnpj,
			offset: currentPage * perPage,
			limit: perPage
		});

		try {
			const json = await get(
				`${process.env.REACT_APP_LABS_API}get_midias_labs.php?${params}`
			);

			if (json?.status === 200 && json.items) {
				if (isFirstLoad.current) {
					setNewIds(json.items.map(m => m.id));
					isFirstLoad.current = false;
				} else {
					setNewIds(getNewIds(midias, json.items));
				}
				setMidias(json.items);
				setTotalMidias(json.total || 0);
				setPageCount(Math.ceil(json.total / perPage));
			}
		} catch (error) {
		} finally {
			if (showLoadingMessage) {
				context.hideLoading();
			}
			setLoading(false);
		}
	};

	const handleAddMidia = () => {
		setEditingMidia(null);
		setShowForm(true);
	};

	const handleEditMidia = (midia) => {
		setEditingMidia(midia);
		setShowForm(true);
	};

	const handleDeleteSelected = async () => {
		if (selectAllMode) {
			await handleDeleteAllInLab();
			return;
		}

		if (selectedIds.length === 0) {
			return;
		}

		const count = selectedIds.length;
		const result = await Swal.fire({
			title: `Apagar ${count} ${count === 1 ? 'mídia' : 'mídias'}?`,
			text: 'Essa ação irá remover as mídias selecionadas. Não é possível desfazer.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			cancelButtonColor: '#3085d6',
			confirmButtonText: 'Sim, apagar',
			cancelButtonText: 'Cancelar',
		});

		if (!(result.isConfirmed || result.value)) {
			return;
		}

		context.showLoading(`Apagando ${count} ${count === 1 ? 'mídia' : 'mídias'}...`);

		try {
			const results = await Promise.all(
				selectedIds.map((id) =>
					del(`${process.env.REACT_APP_LABS_API}delete_midia_labs.php?midia_id=${id}`)
						.then((json) => ({ id, ok: json?.status === 200, json }))
						.catch(() => ({ id, ok: false }))
				)
			);

			setSelectedIds([]);
			await fetchMidias(false);
			context.hideLoading();

			const failed = results.filter((r) => !r.ok);
			if (failed.length === 0) {
				await Swal.fire('Sucesso!', 'Mídias excluídas com sucesso.', 'success');
			} else if (failed.length < selectedIds.length) {
				await Swal.fire(
					'Parcial',
					`${selectedIds.length - failed.length} de ${selectedIds.length} mídias foram excluídas.`,
					'warning'
				);
			} else {
				await Swal.fire('Erro!', 'Erro ao excluir mídias.', 'error');
			}
		} catch (error) {
			context.hideLoading();
			await Swal.fire('Erro!', 'Erro ao excluir mídias.', 'error');
		}
	};

	const handleDeleteAllInLab = async () => {
		const count = totalMidias;
		const result = await Swal.fire({
			title: `Apagar todas as ${count} mídias?`,
			text: 'Essa ação irá remover TODAS as mídias deste laboratório, em todas as páginas. Não é possível desfazer.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			cancelButtonColor: '#3085d6',
			confirmButtonText: 'Sim, apagar tudo',
			cancelButtonText: 'Cancelar',
		});

		if (!(result.isConfirmed || result.value)) {
			return;
		}

		const labCnpj = isLabUser ? context.userSession.conta : selectedLab?.cnpj;
		context.showLoading(`Apagando todas as ${count} mídias...`);

		try {
			const json = await post(
				`${process.env.REACT_APP_LABS_API}delete_all_midias_labs.php`,
				{ lab_cnpj: labCnpj }
			);

			setSelectAllMode(false);
			setSelectedIds([]);
			setCurrentPage(0);
			await fetchMidias(false);
			context.hideLoading();

			if (json?.status === 200) {
				await Swal.fire(
					'Sucesso!',
					`Todas as ${count} mídias foram excluídas.`,
					'success'
				);
			} else {
				await Swal.fire(
					'Erro!',
					json?.message || 'Erro ao excluir mídias.',
					'error'
				);
			}
		} catch (error) {
			context.hideLoading();
			await Swal.fire('Erro!', 'Erro ao excluir mídias.', 'error');
		}
	};

	const handleDeleteMidia = async (midiaId) => {
		const result = await Swal.fire({
			title: 'Atenção',
			text: 'Tem certeza que deseja excluir esta peça?',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
			confirmButtonText: 'Sim',
			cancelButtonText: 'Não'
		});

		if (result.isConfirmed || result.value) {
			context.showLoading('Excluindo mídia...');

			try {
				const json = await del(
					`${process.env.REACT_APP_LABS_API}delete_midia_labs.php?midia_id=${midiaId}`
				);

				if (json?.status === 200) {
					await fetchMidias(false);
					context.hideLoading();
					await Swal.fire('Sucesso!', 'Mídia excluída com sucesso.', 'success');
				} else {
					context.hideLoading();
					await Swal.fire('Erro!', json?.message || 'Erro ao excluir mídia.', 'error');
				}
			} catch (error) {
				context.hideLoading();
				await Swal.fire('Erro!', 'Erro ao excluir mídia.', 'error');
			}
		}
	};

	const handleFormClose = () => {
		setShowForm(false);
		setEditingMidia(null);
	};

	const handleFormSave = async () => {
		await fetchMidias(false);
	};

	const handlePageClick = (e) => {
		setCurrentPage(e.selected);
	};

	return (
		<>
			{showForm && (
				<MidiaLabsForm
					midia={editingMidia}
					labCnpj={isLabUser ? context.userSession.conta : selectedLab?.cnpj}
					onClose={handleFormClose}
					onSave={handleFormSave}
				/>
			)}

			<div className="midia-oticas">
				{hasUploadPermission && midias.length > 0 && (
					<Toolbar variant="dense" disableGutters className="midia-oticas__toolbar">
						<FormControlLabel
							control={
								<Checkbox
									checked={selectAllMode || allSelected}
									indeterminate={!selectAllMode && someSelected}
									onChange={(event) => toggleSelectAll(event.target.checked)}
									color="primary"
								/>
							}
							label={(selectAllMode || allSelected) ? 'Desmarcar todas' : 'Selecionar todas'}
							className="midia-oticas__select-all"
						/>

						<div className="midia-oticas__toolbar-spacer" />

						{showActionsRow && (
							<Button
								variant="outlined"
								size="large"
								onClick={handleDeleteSelected}
								className="midia-oticas-bulk-delete-btn"
								startIcon={<NodeMinus size={22} />}
							>
								{selectAllMode
									? `Apagar todas as ${totalMidias} mídias`
									: `Apagar ${effectiveCount} ${effectiveCount === 1 ? 'selecionada' : 'selecionadas'}`}
							</Button>
						)}
					</Toolbar>
				)}

				{hasUploadPermission && midias.length > 0 && (
					<Collapse
						in={showExpandBanner || showActiveBanner}
						unmountOnExit
						onExited={() => setBannerContent(null)}
						className="midia-oticas__banner-collapse">
						<div className="midia-oticas__banner">
							{bannerContent === 'expand' && (
								<Alert
									severity="info"
									action={
										<Button
											color="inherit"
											size="small"
											onClick={activateSelectAllMode}
											className="midia-oticas__banner-action">
											Selecionar todas as {totalMidias} mídias do laboratório
										</Button>
									}>
									As {midias.length} mídias desta página estão selecionadas.
								</Alert>
							)}
							{bannerContent === 'active' && (
								<Alert
									severity="success"
									action={
										<Button
											color="inherit"
											size="small"
											onClick={clearSelectAllMode}
											className="midia-oticas__banner-action">
											Limpar seleção
										</Button>
									}>
									Todas as {totalMidias} mídias do laboratório estão selecionadas.
								</Alert>
							)}
						</div>
					</Collapse>
				)}

				{midias.length > 0 && (
				<div className="midia-oticas__cards-grid">
					{hasUploadPermission && (
						<Card
							className="midia-oticas__add-card"
							onClick={handleAddMidia}
							id="midia-oticas-add-card">
							<Card.Body className="d-flex flex-column justify-content-center align-items-center">
								<Plus size='60' className="text-primary mb-3"/>
								<p className="text-muted">{MESSAGES.MIDIA_LABS_CLICK_TO_ADD}</p>
							</Card.Body>
						</Card>
					)}

					{midias.map((midia, index) => {
						const isNew = newIds.includes(midia.id);
						return isNew ? (
							<Grow
								key={midia.id}
								in={true}
								style={{ transformOrigin: '0 0 0' }}
								timeout={500 + index * 100}
							>
								<div>
									<MidiaLabsCardSingle
										midia={midia}
										hasEditPermission={hasUploadPermission}
										selected={selectAllMode || selectedIds.includes(midia.id)}
										onSelectChange={(checked) => toggleSelect(midia.id, checked)}
										onEdit={() => handleEditMidia(midia)}
										onDelete={() => handleDeleteMidia(midia.id)}
									/>
								</div>
							</Grow>
						) : (
							<div key={midia.id}>
								<MidiaLabsCardSingle
									midia={midia}
									hasEditPermission={hasUploadPermission}
									selected={selectAllMode || selectedIds.includes(midia.id)}
									onSelectChange={(checked) => toggleSelect(midia.id, checked)}
									onEdit={() => handleEditMidia(midia)}
									onDelete={() => handleDeleteMidia(midia.id)}
								/>
							</div>
						);
					})}
				</div>
				)}

				{midias.length === 0 && !loading && (
					<EmptyState
						icon={Collection}
						title="Nenhuma mídia disponível"
						description={hasUploadPermission ? "Adicione mídias para disponibilizar às óticas." : null}
						actionLabel={hasUploadPermission ? "Adicionar mídia" : null}
						actionIcon={Plus}
						onAction={handleAddMidia}
					/>
				)}

				{pageCount > 1 && (
					<div className="pagination-container">
						<ReactPaginate
							previousLabel={"Anterior"}
							nextLabel={"Próximo"}
							breakLabel={"..."}
							breakClassName={"break-me"}
							pageCount={pageCount}
							marginPagesDisplayed={2}
							pageRangeDisplayed={2}
							onPageChange={handlePageClick}
							containerClassName={"pagination"}
							subContainerClassName={"pages pagination"}
							activeClassName={"active"}
							forcePage={currentPage}
							id="midia-oticas-pagination"
						/>
					</div>
				)}
			</div>

			{hasUploadPermission && (
				<AppBar position="fixed" className="midia-oticas-bottom-bar">
					<Toolbar className="midia-oticas-bottom-bar__toolbar">
						{midias.length > 0 && (
							<FormControlLabel
								control={
									<Checkbox
										checked={selectAllMode || allSelected}
										indeterminate={!selectAllMode && someSelected}
										onChange={(event) => toggleSelectAll(event.target.checked)}
										size="small"
										className="midia-oticas-bottom-bar__checkbox"
									/>
								}
								label={(selectAllMode || allSelected) ? 'Desmarcar' : 'Selecionar'}
								className="midia-oticas-bottom-bar__select"
							/>
						)}

						<Fab
							aria-label="adicionar mídia"
							className="midia-oticas-bottom-bar__fab"
							onClick={handleAddMidia}
						>
							<Plus size={24} />
						</Fab>

						<div style={{ flexGrow: 1 }} />

						{showActionsRow && (
							<IconButton className="midia-oticas-bottom-bar__action-btn" onClick={handleDeleteSelected}>
								<Badge badgeContent={effectiveCount} color="error">
									<NodeMinus size={22} />
								</Badge>
							</IconButton>
						)}
					</Toolbar>
				</AppBar>
			)}
		</>
	);
};

export default MidiaOticas;